/**
 * Choosing a colour. §6.3.
 *
 * This is a print tool, and the difference from every RGB picker a customer has
 * used before is worth being deliberate about.
 *
 * A press lays down four inks. Black text set as 0/0/0/100 prints on one plate
 * and is crisp at 7 pt; the same black arrived at by converting #000000 through
 * a profile is four plates, and any press a hair out of register shows coloured
 * fringes on the edges of small type. The whole pipeline behind this editor has
 * been carrying CMYK carefully from the document to the plate - so the picker
 * must not be the place it gets thrown away.
 *
 * Hence the arrangement: swatches for the customer who wants their brand red
 * and nothing else, hex for the one who has a hex code from their designer,
 * and the four ink boxes underneath for anyone who knows what they mean. Hex
 * is converted on the way in, once, and #000000 becomes K alone rather than a
 * four-colour black.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { Colour, Ink } from '../model/types';
import { coverage, toCss } from '../model/units';
import { useEditor } from '../state/store';
import { reach } from '../colour/gamut';
import { inkToHex, rgbToInk } from '../colour/convert';
import { coloursIn, inkKey, recentColours, rememberColour } from '../colour/used';
import { Spectrum } from './Spectrum';

interface Props {
  label: string;
  value: Colour | null;
  onChange: ( colour: Colour ) => void;
  /** Offered when the thing can have no colour at all, like a stroke. */
  onClear?: () => void;
}

/**
 * The house palette.
 *
 * Real ink values rather than hex, because these are the ones the press will
 * be given. The reds are Prismat's own; the rest is a working set that covers
 * most of what a business card needs without anybody having to think.
 */
const SWATCHES: Array<{ name: string; ink: Ink }> = [
  { name: 'Black', ink: [ 0, 0, 0, 1 ] },
  { name: 'Rich black', ink: [ 0.6, 0.4, 0.4, 1 ] },
  { name: 'Grey 75', ink: [ 0, 0, 0, 0.75 ] },
  { name: 'Grey 50', ink: [ 0, 0, 0, 0.5 ] },
  { name: 'Grey 25', ink: [ 0, 0, 0, 0.25 ] },
  { name: 'White', ink: [ 0, 0, 0, 0 ] },

  { name: 'Prismat red', ink: [ 0, 0.9, 0.86, 0.12 ] },
  { name: 'Deep red', ink: [ 0.15, 1, 0.9, 0.1 ] },
  { name: 'Orange', ink: [ 0, 0.55, 0.95, 0 ] },
  { name: 'Amber', ink: [ 0, 0.3, 1, 0 ] },
  { name: 'Gold', ink: [ 0.1, 0.25, 0.85, 0.05 ] },
  { name: 'Olive', ink: [ 0.25, 0.2, 0.9, 0.15 ] },

  { name: 'Green', ink: [ 0.8, 0.1, 1, 0.05 ] },
  { name: 'Teal', ink: [ 0.85, 0.2, 0.4, 0.05 ] },
  { name: 'Sky', ink: [ 0.7, 0.15, 0, 0 ] },
  { name: 'Blue', ink: [ 1, 0.7, 0, 0.05 ] },
  { name: 'Navy', ink: [ 1, 0.8, 0.2, 0.4 ] },
  { name: 'Purple', ink: [ 0.6, 0.85, 0, 0 ] },
];

/** Total ink a press can take before the sheet stops drying. */
const INK_LIMIT = 300;

export function ColourField( { label, value, onChange, onClear }: Props ) {
  const [ open, setOpen ] = useState( false );
  const wrap = useRef<HTMLDivElement>( null );

  useEffect( () => {
    if ( ! open ) return;

    const away = ( event: MouseEvent ) => {
      // A click on the canvas while the eyedropper is out is the answer to
      // this picker's question, not a click away from it.
      if ( useEditor.getState().sampling ) return;

      if ( wrap.current && ! wrap.current.contains( event.target as Node ) ) setOpen( false );
    };

    const key = ( event: KeyboardEvent ) => {
      // Escape while the eyedropper is out puts the dropper down, not this.
      // Otherwise it closes this and is marked handled, caught before the
      // editor's shortcuts, so it does not also deselect the object.
      if ( event.key !== 'Escape' || useEditor.getState().sampling ) return;

      event.preventDefault();
      setOpen( false );
    };

    window.addEventListener( 'mousedown', away );
    window.addEventListener( 'keydown', key, true );

    return () => {
      window.removeEventListener( 'mousedown', away );
      window.removeEventListener( 'keydown', key, true );
    };
  }, [ open ] );

  return (
    <div className="tb-colour" ref={ wrap }>
      <button
        type="button"
        className="tb-colour__button"
        aria-expanded={ open }
        onClick={ () => setOpen( ! open ) }
      >
        <Chip colour={ value } />
        <span className="tb-colour__label">{ label }</span>
        <span className="tb-colour__value">{ describe( value ) }</span>
      </button>

      { open && (
        <div className="tb-colour__pop" role="dialog" aria-label={ label }>
          <InkEditor value={ value } onChange={ onChange } onClear={ onClear } close={ () => setOpen( false ) } />
        </div>
      ) }
    </div>
  );
}

/** The little square. A chequerboard behind it, so "no colour" reads as empty. */
export function Chip( { colour }: { colour: Colour | null } ) {
  if ( ! colour ) return <span className="tb-colour__chip tb-colour__chip--none" />;

  return <span className="tb-colour__chip" style={ { background: toCss( colour ) } } />;
}

export function InkEditor( {
  value,
  onChange,
  onClear,
  close,
}: {
  value: Colour | null;
  onChange: ( colour: Colour ) => void;
  onClear?: () => void;
  close?: () => void;
} ) {
  const ink = inkOf( value );

  /*
   * The screen colour last picked from the spectrum or typed as hex, while the
   * ink is still the one it became. Ink shows as it prints, which for a vivid
   * screen colour is duller - and following that back into the spectrum would
   * make the handle jump away from under the pointer.
   */
  const picked = useRef<{ hex: string; ink: Ink } | null>( null );
  const screen = picked.current && sameInk( picked.current.ink, ink ) ? picked.current.hex : toHex( ink );
  const [ hex, setHex ] = useState( screen );
  const [ showInks, setShowInks ] = useState( false );

  const doc = useEditor( ( s ) => s.doc );
  const sampling = useEditor( ( s ) => !! s.sampling );
  const used = useMemo( () => coloursIn( doc ), [ doc ] );
  const [ recent ] = useState( recentColours );

  /*
   * Whether ink can print what the screen is showing. Worked out from the
   * screen colour rather than the ink, because the screen colour is what the
   * customer is judging - and the answer is only ever a warning.
   */
  const verdict = useMemo( () => reach( screen ), [ screen ] );

  // The hex box follows the colour while the customer is choosing elsewhere,
  // but must not fight them while they are typing in it.
  const typing = useRef( false );

  useEffect( () => {
    if ( ! typing.current ) setHex( screen );
  }, [ ink[ 0 ], ink[ 1 ], ink[ 2 ], ink[ 3 ] ] );

  /*
   * Remembered when the picker closes, not on every change: a drag across the
   * spectrum is a hundred colours, and only the one it stopped on was chosen.
   */
  const last = useRef<Colour | null>( null );
  const first = useRef( value );

  last.current = value;

  useEffect( () => () => {
    if ( last.current && last.current !== first.current ) rememberColour( last.current );
  }, [] );

  const setInk = ( next: Ink ) => onChange( { space: 'cmyk', value: next } );

  const total = Math.round( ink.reduce( ( sum, v ) => sum + v, 0 ) * 100 );

  return (
    <>
      { used.length > 0 && <ColourRow label="In this design" colours={ used } ink={ ink } onPick={ onChange } /> }
      { recent.length > 0 && <ColourRow label="Recent" colours={ recent } ink={ ink } onPick={ onChange } /> }

      <div className="tb-colour__swatches">
        { SWATCHES.map( ( swatch ) => (
          <button
            type="button"
            key={ swatch.name }
            className={ `tb-colour__swatch${ sameInk( ink, swatch.ink ) ? ' is-on' : '' }` }
            style={ { background: toCss( { space: 'cmyk', value: swatch.ink } ) } }
            title={ `${ swatch.name } — ${ inkText( swatch.ink ) }` }
            aria-label={ swatch.name }
            onClick={ () => setInk( swatch.ink ) }
          />
        ) ) }
      </div>

      <Spectrum
        hex={ screen }
        onPick={ ( chosen ) => {
          const parsed = fromHex( chosen );

          if ( parsed ) {
            picked.current = { hex: chosen.toLowerCase(), ink: parsed };
            setInk( parsed );
          }
        } }
      />

      <div className="tb-colour__hexrow">
        <label className="tb-field">
          <span className="tb-field__label">Hex</span>
          <input
            className="tb-input"
            value={ hex }
            spellCheck={ false }
            onFocus={ () => { typing.current = true; } }
            onBlur={ () => { typing.current = false; setHex( screen ); } }
            onChange={ ( event ) => {
              const next = event.target.value;

              setHex( next );

              const parsed = fromHex( next );

              if ( parsed ) {
                picked.current = { hex: '#' + next.trim().replace( /^#/, '' ).toLowerCase(), ink: parsed };
                setInk( parsed );
              }
            } }
          />
        </label>

        {/*
          Picks from the design, not the screen: clicking the logo's red gives
          back the logo's own ink, so the copy prints as the same red.
        */}
        <button
          type="button"
          className={ `tb-btn tb-btn--icon tb-colour__dropper${ sampling ? ' is-on' : '' }` }
          aria-pressed={ sampling }
          title={ sampling ? 'Click anything in the design - Escape to stop' : 'Pick a colour from the design' }
          aria-label="Pick a colour from the design"
          onClick={ () => {
            const store = useEditor.getState();

            if ( store.sampling ) store.endSampling();
            else store.beginSampling( ( picked ) => onChange( picked ) );
          } }
        >
          <IconDropper />
        </button>
      </div>

      {/*
        The screen can show colours ink cannot reach, and a spectrum offers
        plenty of them. Said here, with a chip of roughly what will come back,
        so the surprise happens now rather than when the cards arrive.
      */}
      { verdict && ! verdict.ok && (
        <p className="tb-colour__gamut" role="status">
          <span className="tb-colour__chip" style={ { background: toHex( ink ) } } />
          <span>Brighter on screen than ink can print. Expect something closer to this.</span>
        </p>
      ) }

      <button
        type="button"
        className="tb-colour__more"
        aria-expanded={ showInks }
        onClick={ () => setShowInks( ! showInks ) }
      >
        { showInks ? 'Hide the inks' : 'Set the inks exactly' }
      </button>

      { showInks && (
        <div className="tb-colour__inks">
          { ( [ 'Cyan', 'Magenta', 'Yellow', 'Black' ] as const ).map( ( name, index ) => (
            <label className="tb-field" key={ name }>
              <span className="tb-field__label">{ name.charAt( 0 ) }</span>
              <input
                className="tb-input"
                type="number"
                min={ 0 }
                max={ 100 }
                step={ 1 }
                value={ Math.round( ( ink[ index ] ?? 0 ) * 100 ) }
                aria-label={ name }
                onChange={ ( event ) => {
                  const next = ink.slice() as Ink;

                  next[ index ] = clamp01( parseFloat( event.target.value ) / 100 );
                  setInk( next );
                } }
              />
            </label>
          ) ) }

          {/*
            Total ink, which is a press limit rather than a design one. Over it
            the sheet does not dry, and the printer will ask for the file again.
          */}
          <p className={ `tb-colour__total${ total > INK_LIMIT ? ' is-over' : '' }` }>
            { total }% ink
            { total > INK_LIMIT ? ` — over the ${ INK_LIMIT }% a press can take` : '' }
          </p>
        </div>
      ) }

      { onClear && (
        <button
          type="button"
          className="tb-btn tb-btn--quiet tb-colour__clear"
          onClick={ () => { onClear(); close?.(); } }
        >
          No colour
        </button>
      ) }
    </>
  );
}

/** A labelled row of colour chips: this design's colours, or recent ones. */
function ColourRow( {
  label,
  colours,
  ink,
  onPick,
}: {
  label: string;
  colours: Colour[];
  ink: Ink;
  onPick: ( colour: Colour ) => void;
} ) {
  return (
    <div className="tb-colour__row">
      <span className="tb-colour__rowlabel">{ label }</span>
      <div className="tb-colour__swatches tb-colour__swatches--row">
        { colours.map( ( colour ) => (
          <button
            type="button"
            key={ inkKey( colour ) }
            className={ `tb-colour__swatch${ sameInk( ink, inkOf( colour ) ) ? ' is-on' : '' }` }
            style={ { background: toCss( colour ) } }
            title={ describe( colour ) }
            aria-label={ `${ label }: ${ describe( colour ) }` }
            onClick={ () => onPick( colour ) }
          />
        ) ) }
      </div>
    </div>
  );
}

/** A pipette. Drawn here rather than in icons.tsx because nothing else uses it. */
function IconDropper() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m2 22 1-1h3l9-9" />
      <path d="M3 21v-3l9-9" />
      <path d="m15 6 3.4-3.4a2.1 2.1 0 1 1 3 3L18 9l.4.4a2.1 2.1 0 1 1-3 3l-3.8-3.8a2.1 2.1 0 1 1 3-3l.4.4Z" />
    </svg>
  );
}

/* ---------------------------------------------------------------
 * Ink and hex
 * ------------------------------------------------------------ */

const clamp01 = ( v: number ) => ( Number.isFinite( v ) ? Math.min( 1, Math.max( 0, v ) ) : 0 );

export function inkOf( colour: Colour | null ): Ink {
  if ( ! colour ) return [ 0, 0, 0, 0 ];
  if ( colour.space === 'cmyk' ) return colour.value;
  if ( colour.space === 'spot' ) return colour.fallback;

  return fromHex( colour.value ) ?? [ 0, 0, 0, 1 ];
}

const sameInk = ( a: Ink, b: Ink ) => a.every( ( v, i ) => Math.abs( v - ( b[ i ] ?? 0 ) ) < 0.005 );

function inkText( ink: Ink ): string {
  return ink.map( ( v ) => Math.round( v * 100 ) ).join( '/' );
}

/** What the button says: an ink recipe, or a spot name, or nothing. */
export function describe( colour: Colour | null ): string {
  if ( ! colour ) return 'None';
  if ( colour.space === 'spot' ) return colour.value;
  if ( colour.space === 'rgb' ) return colour.value.toUpperCase();

  const over = coverage( colour );

  return inkText( colour.value ) + ( over !== null && over > INK_LIMIT ? ' !' : '' );
}

export function toHex( ink: Ink ): string {
  return inkToHex( ink );
}

/**
 * Hex to the ink that prints like it.
 *
 * Through the same conversion as the press file (colour/convert.ts), with
 * greys - black most of all - on black ink alone, so #000000 is 0/0/0/100
 * rather than a four-plate black that fringes on small type.
 */
export function fromHex( text: string ): Ink | null {
  const clean = text.trim().replace( /^#/, '' );

  const full = clean.length === 3
    ? clean.split( '' ).map( ( ch ) => ch + ch ).join( '' )
    : clean;

  if ( ! /^[0-9a-f]{6}$/i.test( full ) ) return null;

  return rgbToInk( parseInt( full.slice( 0, 2 ), 16 ), parseInt( full.slice( 2, 4 ), 16 ), parseInt( full.slice( 4, 6 ), 16 ) );
}

